import React from 'react';

const HEX = /^[a-f0-9]{64}$/;
export const VERDICT_REQUIRED_COPY = 'Complete the QA checklist and record the developer verdict';

export function verdictContext(mav, jobId, revisionDigest) {
  return JSON.stringify([mav?.__contextKey, mav?.__contextEpoch, mav?.currentUser?.id,
    mav?.activeWorkspaceId, mav?.viewerContext, mav?.contextLoad?.status, jobId, revisionDigest]);
}

export function verdictError(code) {
  return ({
    developer_verdict_required: VERDICT_REQUIRED_COPY,
    developer_verdict_historical: 'This verdict belongs to older evidence. Reload the current QA checklist.',
    developer_verdict_revision_required: 'Load the current revision before recording its verdict.',
    developer_verdict_changed: 'The recorded verdict changed. Reload the current checklist before approval.',
    developer_verdict_measurement_required: 'This revision needs bound full-email QA evidence. Recertify it before review.',
    developer_verdict_measurement_unbound: 'The QA measurements do not match this revision. Recertify before review.',
    developer_verdict_safety_refused: 'Safety findings block approval. Request corrections and rerun QA; acknowledgment cannot waive them.',
    developer_verdict_checklist_required: 'Review every contract decision, including omissions and the whole-email row.',
    developer_verdict_acknowledgment_required: 'Write an acknowledgment of the named warnings and unavailable measurements.',
    developer_verdict_correction_required: 'Choose a correction type and explain what must change.',
    developer_verdict_action_conflict: 'Accept every row to record a positive verdict, or mark a correction to request amendments.',
    qualification_review_changed: 'Another review was saved. Reload the checklist and review its latest history.',
    qualification_report_changed: 'The QA evidence changed. Reload the current checklist.',
    qualification_report_unavailable: 'The QA review report is unavailable. Reload or recertify this revision.',
    qualification_producer_unverified: 'The original scorer report is not producer-verified; it cannot certify this revision.',
    qualification_response_unbound: 'The returned verdict does not match this workspace and revision evidence.',
    qualification_review_unavailable: 'The QA checklist could not be loaded. Approval remains blocked.',
    qualification_request_invalid: 'Review every checklist row and provide the required correction or acknowledgment notes.',
    qualification_self_review: 'An independent reviewer must record this verdict.',
    self_review: 'An independent reviewer must record this verdict.',
    qualification_withdraw_not_owner: 'Only the reviewer who recorded this verdict can withdraw it.',
    qualification_withdraw_reason_required: 'A written reason is required to withdraw a verdict.',
    qualification_forbidden: 'Your current workspace permissions do not permit this review.',
  })[code] || String(code || 'qualification_review_unavailable').replace(/_/g, ' ');
}

// This is UI response binding, not a replacement for the server's report validator.
export function bindCurrentReview(value, { jobId, workspaceId }) {
  const report = value?.report;
  if (value?.schema !== 'QualificationReview.v1' || value.job_id !== jobId || value.workspace_id !== workspaceId
    || report?.schema_version !== 'CurrentRevisionQaReviewReport.v1' || report.job_id !== jobId || report.workspace_id !== workspaceId
    || report.revision !== value.revision || report.revision_digest !== value.revision_digest
    || !HEX.test(report.report_digest || '') || !HEX.test(report.revision_digest || '')
    || report.original_sabre_qualification !== false || report.promotion_performed !== false
    || value.promotion_performed !== false || value.revision_approval_changed !== false
    || value.report_verification !== 'current_editor_evidence' || typeof value.current !== 'boolean'
    || typeof value.developer_verdict_required !== 'boolean' || typeof value.can_review !== 'boolean'
    || !Array.isArray(value.events) || !Array.isArray(value.correction_codes)
    || !Array.isArray(report.decisions) || !report.decisions.length
    || !Array.isArray(report.blocking_findings) || report.measurement?.scope !== 'full_email'
    || !report.measurement.axes || typeof report.measurement.axes !== 'object'
    || !['warnings','unmeasured','comparison_unmeasured','stale_expensive'].every(key => Array.isArray(report.measurement[key]))
    || !['PASS', 'CONDITIONAL', 'FAIL'].includes(report.status)) throw Error('qualification_response_unbound');
  const ids = new Set();
  for (const row of report.decisions) {
    if (!row.region_id || ids.has(row.region_id) || !HEX.test(row.decision_digest || '')
      || typeof row.acknowledgment_required !== 'boolean'
      || row.measurement_scope !== (row.region_id === 'review:full-email' ? 'full_email' : 'full_email_not_per_region')) throw Error('qualification_response_unbound');
    ids.add(row.region_id);
  }
  if (!ids.has('review:full-email')) throw Error('qualification_response_unbound');
  if (value.active && (value.active.schema_version !== 'CurrentRevisionDeveloperVerdict.v1'
    || value.active.report_digest !== report.report_digest || value.active.revision_digest !== report.revision_digest
    || value.active.revision !== report.revision || value.active.checklist_digest !== report.checklist_digest
    || !['approve', 'reject'].includes(value.active.action) || !HEX.test(value.active.verdict_digest || ''))) throw Error('qualification_response_unbound');
  return value;
}

export function currentVerdictReady(review, revisionDigest) {
  const data = review?.data, report = data?.report, active = data?.active, binding = data?.approval_binding;
  return !!(review?.state === 'ready' && !review.busy && !review.error && !review.uncertain
    && data?.current === true && data.developer_verdict_required === false
    && report?.revision_digest === revisionDigest && ['PASS', 'CONDITIONAL'].includes(report.status)
    && report.blocking_findings.length === 0 && active?.action === 'approve'
    && binding?.schema === 'CurrentRevisionVerdictBinding.v1' && binding.job_id === data.job_id
    && binding.workspace_id === data.workspace_id && binding.revision === report.revision
    && binding.revision_digest === revisionDigest && binding.report_digest === report.report_digest
    && binding.verdict_digest === active.verdict_digest && HEX.test(binding.event_digest || '')
    && binding.event_digest === data.last_event_digest);
}

export function currentChecklistComplete(rows, choices) {
  return rows.length > 0 && rows.every(row => {
    const choice = choices[row.region_id];
    return choice?.action === 'accept' ? !row.acknowledgment_required || !!choice.note?.trim()
      : choice?.action === 'correct' && !!choice.code && !!choice.note?.trim();
  });
}

export function currentChecklistBody(report, data, choices, action, note = '') {
  return { report_digest: report.report_digest, expected_event_digest: data.last_event_digest, action, note: note.trim(),
    region_decisions: action === 'withdraw' ? [] : report.decisions.map(row => {
      const choice = choices[row.region_id];
      return { region_id: row.region_id, decision_digest: row.decision_digest, action: choice.action,
        correction_codes: choice.action === 'correct' ? [choice.code] : [],
        note: choice.action === 'correct' || row.acknowledgment_required ? (choice.note || '').trim() : '' };
    }) };
}

export function useCurrentQualificationReview({ jobId, revisionDigest, enabled, refreshKey, writable = true }) {
  const mav = window.MAV, identity = verdictContext(mav, jobId, revisionDigest);
  const scope = identity + ':' + String(refreshKey);
  const [state, setState] = React.useState({ scope: null, state: 'loading', data: null, busy: false, error: null, uncertain: false });
  const serial = React.useRef(0), latest = React.useRef(scope), pending = React.useRef(null), writing = React.useRef(false);
  const authority = React.useRef(null); authority.current = state.scope === scope ? state : null;
  latest.current = scope;
  const allowed = React.useRef(writable); allowed.current = writable;
  const validScope = () => latest.current === scope && identity === verdictContext(window.MAV, jobId, revisionDigest);
  const request = async (body = null, recover = false, historicalDigest = null) => {
    if (!enabled || !revisionDigest || !validScope() || writing.current) return;
    if (body) {
      const action = recover ? pending.current?.body?.action : body.action, retained = authority.current;
      if (action !== 'withdraw' && !allowed.current) return;
      if (!recover && (retained?.busy || retained?.error || retained?.uncertain)) return;
      if (!recover && (action === 'withdraw'
        ? retained?.data?.can_withdraw !== true || retained.data.active?.reviewer?.principal_id !== window.MAV?.currentUser?.id
        : retained?.state !== 'ready' || retained.data?.current !== true || retained.data?.can_review !== true)) return;
    }
    if (body && pending.current?.scope === scope && !recover) return;
    const epoch = ++serial.current;
    let intent = null;
    if (body) {
      intent = recover ? pending.current : { scope, body: JSON.parse(JSON.stringify(body)),
        key: Array.from(crypto.getRandomValues(new Uint8Array(24)), v => v.toString(16).padStart(2, '0')).join('') };
      if (!intent || intent.scope !== scope) return;
      pending.current = intent; writing.current = intent;
    }
    setState(s => ({ ...s, scope, state: 'loading', busy: true, error: null }));
    try {
      if (!window.GooseAPI?.currentQualificationReview) throw Error('qualification_review_unavailable');
      const value = bindCurrentReview(await (historicalDigest
        ? window.GooseAPI.historicalQualificationReview(jobId, historicalDigest)
        : window.GooseAPI.currentQualificationReview(jobId, intent?.body || null, intent?.key || null)),
        { jobId, workspaceId: mav?.activeWorkspaceId });
      if (historicalDigest && (value.report.report_digest !== historicalDigest || value.current !== false)) throw Error('qualification_response_unbound');
      if (serial.current !== epoch || !validScope()) return;
      if (intent) pending.current = null;
      setState({ scope, state: value.current && value.revision_digest === revisionDigest ? 'ready' : 'historical',
        data: value, busy: false, error: null, uncertain: pending.current?.scope === scope });
    } catch (error) {
      if (serial.current !== epoch || !validScope()) return;
      const uncertain = !!intent && !error.status;
      if (intent && !uncertain) pending.current = null;
      setState(s => ({ ...s, scope, state: 'error', busy: false,
        error: error?.data?.code || error.message || 'qualification_review_unavailable', uncertain: uncertain || pending.current?.scope === scope }));
    } finally { if (intent && writing.current === intent) writing.current = false; }
  };
  React.useEffect(() => {
    pending.current = null; writing.current = false;
    setState({ scope, state: 'loading', data: null, busy: false, error: null, uncertain: false });
    request();
    return () => { serial.current++; };
  }, [scope, enabled]);
  React.useEffect(() => {
    const refresh = () => { if (document.visibilityState !== 'hidden') request(); };
    window.addEventListener('focus', refresh); document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [scope, enabled]);
  const shown = state.scope === scope ? state : { state: 'loading', data: null, busy: true, error: null, uncertain: false };
  return { ...shown, identity, blocked: !writable, reload: () => request(), submit: request,
    history: digest => request(null, false, digest),
    recoveryAction: pending.current?.scope === scope ? pending.current.body.action : null,
    recover: () => pending.current?.scope === scope && request(pending.current.body, true) };
}
